import { useState } from "preact/hooks"
import useSWR from "swr"
import { Button } from "../../components/ui/button"
import { CollapsibleSection } from "../../components/ui/collapsible_section"
import { api_fetch } from "../../api/client"
import { exactCurrencyFormatter } from "../../utils"
import { ForecastCustomerMapSelect } from "./forecast_customer_map_select.tsx"
import { ForecastProductMapSelect } from "./forecast_product_map_select.tsx"
import { toast } from "../../components/ui/use-toast"
import { findAccountingIntegration, getIntegrationsWithJobs, type IntegrationWithJobInfo } from "../../api/integration"
import { ForecastDirectorySection } from "./components/forecast_directory"

interface ForecastProductSuggestion {
  product_id: string
  score: number
  match_summary: string
}

interface UnmappedDescription {
  normalized_description: string
  example_description: string
  line_count: number
  total_amount: string
  suggested_products: ForecastProductSuggestion[]
}

interface ForecastCustomerSuggestion {
  customer_stable_key: string
  score: number
  match_summary: string
}

interface UnmappedCustomer {
  normalized_customer: string
  example_customer: string
  line_count: number
  suggested_customers: ForecastCustomerSuggestion[]
  /** Why actualize places this customer nowhere (absent from servers before round 5). */
  reason?: string
}

interface MappingProduct {
  id: string
  name: string
  sku?: string | null
  description?: string | null
}

interface MappingCustomerNode {
  stable_key: string
  name: string
}

interface MappingProductAlias {
  id: string
  alias: string
  normalized_alias: string
  product_id?: string | null
}

interface MappingCustomerAlias {
  id: string
  alias: string
  normalized_alias: string
  customer_stable_key: string
}

interface ForecastMappingsResponse {
  unmapped_descriptions: UnmappedDescription[]
  /** Descriptions left off the list because their lines carry no revenue. */
  hidden_zero_revenue_descriptions?: number
  unmapped_customers: UnmappedCustomer[]
  products: MappingProduct[]
  customer_nodes: MappingCustomerNode[]
  product_aliases: MappingProductAlias[]
  customer_aliases: MappingCustomerAlias[]
}

async function fetcher(url: string): Promise<ForecastMappingsResponse> {
  const res = await api_fetch<ForecastMappingsResponse>(url, { method: "GET" })
  if (!res.ok) throw new Error(res.error.message)
  return res.value.data
}

/**
 * What the page calls the accounting system whose lines it maps: "NetSuite" when that is the
 * connection, "QuickBooks" otherwise (the default, and what the lines are for any other provider).
 */
export function mappings_provider_label(integrations: IntegrationWithJobInfo[] | undefined): string {
  return findAccountingIntegration(integrations ?? [])?.name === "netsuite" ? "NetSuite" : "QuickBooks"
}

/** Display name for the accounting system an org has connected, for the "not QuickBooks" notice. */
export function accounting_system_label(name: string | undefined): string | null {
  switch (name) {
    case "quickbooks":
      return "QuickBooks"
    case "netsuite":
      return "NetSuite"
    case "xero":
      return "Xero"
    case "sageintacct":
      return "Sage Intacct"
    default:
      return null
  }
}

/**
 * Whether the mappings page applies to this org: it maps accounting invoice lines, which the
 * QuickBooks sync (`invoice_line_extract`) and the NetSuite invoice-line pull produce; no other
 * provider yields any. Returns the connected system's name for the notice when it does not apply.
 */
export function mappings_apply_to_org(
  integrations: IntegrationWithJobInfo[] | undefined
): { applies: boolean; accounting_system: string | null } {
  if (!integrations) return { applies: true, accounting_system: null }
  const accounting = findAccountingIntegration(integrations)
  return {
    applies: accounting?.name === "quickbooks" || accounting?.name === "netsuite",
    accounting_system: accounting_system_label(accounting?.name),
  }
}

export function ForecastMappingsPage() {
  const { data, error, isLoading, mutate } = useSWR<ForecastMappingsResponse>(
    "/forecast-mappings",
    fetcher,
    { revalidateOnFocus: false }
  )
  const { data: integrations } = useSWR("forecast-mappings-integrations", async () => {
    const res = await getIntegrationsWithJobs()
    return res.ok ? res.value.data : undefined
  })
  const provider = mappings_provider_label(integrations)
  const applicability = mappings_apply_to_org(integrations)

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 space-y-8">
      <header>
        <h1 className="text-2xl font-bold">Forecast Mappings</h1>
        <p className="text-sm text-gray-600 mt-1">
          This page is for brands whose actuals come from {provider}. When {provider} invoices are
          synced, each invoice line names a product (by its description) and a customer; to land as
          actual sales on the forecast grid, that description has to be mapped to a product in your
          catalog and that customer to a customer row in your forecast. Lines that are not mapped are
          skipped when actuals are pulled in, so the two lists below are what still needs a home.
          Mappings are remembered across every forecast version.
        </p>
        {integrations && !applicability.applies ? (
          <p className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            {applicability.accounting_system
              ? `Your accounting system is ${applicability.accounting_system}, not QuickBooks, so there is nothing to map here: your actuals are loaded from ${applicability.accounting_system} by customer and product directly.`
              : "No QuickBooks connection is set up for this account, so there is nothing to map here."}
          </p>
        ) : null}
      </header>

      {isLoading && <div className="text-sm text-gray-500">Loading…</div>}
      {error && <div className="text-sm text-red-600">Failed to load: {String(error)}</div>}

      {data && (
        <>
          <ExistingProductAliasesCard aliases={data.product_aliases} products={data.products} onChanged={() => mutate()} />
          <ExistingCustomerAliasesCard
            aliases={data.customer_aliases}
            customerNodes={data.customer_nodes}
            provider={provider}
            onChanged={() => mutate()}
          />
          <UnmappedDescriptionsCard
            descriptions={data.unmapped_descriptions}
            hidden_zero_revenue={data.hidden_zero_revenue_descriptions ?? 0}
            products={data.products}
            provider={provider}
            onCreated={() => mutate()}
          />
          <UnmappedCustomersCard
            customers={data.unmapped_customers}
            customerNodes={data.customer_nodes}
            provider={provider}
            onCreated={() => mutate()}
          />
          {provider === "NetSuite" ? (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">Products and customers</h2>
              <ForecastDirectorySection />
            </section>
          ) : null}
        </>
      )}
    </div>
  )
}

function UnmappedDescriptionsCard({
  descriptions,
  hidden_zero_revenue,
  products,
  provider,
  onCreated,
}: {
  descriptions: UnmappedDescription[]
  hidden_zero_revenue: number
  products: MappingProduct[]
  provider: string
  onCreated: () => void
}) {
  return (
    <CollapsibleSection
      title="Unmapped invoice line descriptions"
      count={descriptions.length}
      description={`Lines from ${provider} invoices that don't match any product alias yet.${
        hidden_zero_revenue > 0
          ? ` ${hidden_zero_revenue.toLocaleString()} more with no revenue (raw ingredients, display parts) are not listed.`
          : ""
      }`}
      bodyMaxHeight="max-h-[32rem]"
    >
      {descriptions.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">All descriptions are mapped.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase sticky top-0 z-10 bg-white shadow-[0_1px_0_0_theme(colors.gray.200)]">
            <tr>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">Lines</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2 text-left">Map to product</th>
            </tr>
          </thead>
          <tbody>
            {descriptions.map((d) => (
              <DescriptionRow key={d.normalized_description} desc={d} products={products} onCreated={onCreated} />
            ))}
          </tbody>
        </table>
      )}
    </CollapsibleSection>
  )
}

function DescriptionRow({ desc, products, onCreated }: { desc: UnmappedDescription; products: MappingProduct[]; onCreated: () => void }) {
  const [productId, setProductId] = useState<string>("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const top = desc.suggested_products[0]

  async function save() {
    if (!productId) return
    setBusy(true)
    setErr(null)
    try {
      const res = await api_fetch("/forecast-mappings/product-aliases", {
        method: "POST",
        body: { alias: desc.example_description, product_id: productId },
      })
      if (!res.ok) throw new Error(res.error.message)
      onCreated()
      toast({ title: "Product mapping saved", description: "Future actualization runs can include these invoice lines." })
    } catch (e: any) {
      const message = e?.message ?? "Failed"
      setErr(message)
      toast({ title: "Could not save product mapping", description: message, variant: "destructive" })
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr className="border-t border-gray-100">
      <td className="px-4 py-2">{desc.example_description}</td>
      <td className="px-4 py-2 text-right">{desc.line_count}</td>
      <td className="px-4 py-2 text-right">{exactCurrencyFormatter(Number(desc.total_amount))}</td>
      <td className="px-4 py-2">
        <div className="flex flex-col gap-1">
          {top ? (
            <p className="text-xs text-gray-600">
              Top match:{" "}
              <span className="font-medium text-gray-800">
                {products.find((p) => p.id === top.product_id)?.name ?? "Product"}
              </span>{" "}
              ({Math.round(top.score * 100)}% — {top.match_summary})
            </p>
          ) : (
            <p className="text-xs text-gray-500">No strong catalog match — search to map manually.</p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <ForecastProductMapSelect
              products={products}
              suggestions={desc.suggested_products}
              value={productId}
              onValueChange={setProductId}
              className="flex-1 min-w-[260px]"
            />
            <Button size="sm" onClick={save} disabled={!productId || busy}>
              {busy ? "Saving…" : "Map"}
            </Button>
            {err && <span className="text-xs text-red-600">{err}</span>}
          </div>
        </div>
      </td>
    </tr>
  )
}

function ExistingProductAliasesCard({ aliases, products, onChanged }: { aliases: MappingProductAlias[]; products: MappingProduct[]; onChanged: () => void }) {
  const productById = new Map(products.map((p) => [p.id, p]))
  return (
    <CollapsibleSection
      title="Existing product aliases"
      count={aliases.length}
      description="Invoice line descriptions already mapped to a product."
    >
      {aliases.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">No product aliases yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase sticky top-0 z-10 bg-white shadow-[0_1px_0_0_theme(colors.gray.200)]">
            <tr>
              <th className="px-4 py-2 text-left">Alias</th>
              <th className="px-4 py-2 text-left">Product</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {aliases.map((a) => (
              <tr key={a.id} className="border-t border-gray-100">
                <td className="px-4 py-2">{a.alias}</td>
                <td className="px-4 py-2">
                  {a.product_id ? productById.get(a.product_id)?.name ?? a.product_id : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <DeleteAliasButton url={`/forecast-mappings/product-aliases/${a.id}`} onDeleted={onChanged} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CollapsibleSection>
  )
}

function UnmappedCustomersCard({
  customers,
  customerNodes,
  provider,
  onCreated,
}: {
  customers: UnmappedCustomer[]
  customerNodes: MappingCustomerNode[]
  provider: string
  onCreated: () => void
}) {
  return (
    <CollapsibleSection
      title={`Unmapped ${provider} customers`}
      count={customers.length}
      description="Customers seen on invoices that don't yet map to a hierarchy customer node."
      bodyMaxHeight="max-h-[32rem]"
    >
      {customers.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">All customers are mapped.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase sticky top-0 z-10 bg-white shadow-[0_1px_0_0_theme(colors.gray.200)]">
            <tr>
              <th className="px-4 py-2 text-left">{provider} customer</th>
              <th className="px-4 py-2 text-right">Lines</th>
              <th className="px-4 py-2 text-left">Map to forecast customer</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <CustomerRow key={c.normalized_customer} customer={c} customerNodes={customerNodes} provider={provider} onCreated={onCreated} />
            ))}
          </tbody>
        </table>
      )}
    </CollapsibleSection>
  )
}

function CustomerRow({
  customer,
  customerNodes,
  provider,
  onCreated,
}: {
  customer: UnmappedCustomer
  customerNodes: MappingCustomerNode[]
  provider: string
  onCreated: () => void
}) {
  const [stableKey, setStableKey] = useState<string>("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const top = customer.suggested_customers[0]

  async function save() {
    if (!stableKey) return
    setBusy(true)
    setErr(null)
    try {
      const res = await api_fetch("/forecast-mappings/customer-aliases", {
        method: "POST",
        body: { alias: customer.example_customer, customer_stable_key: stableKey },
      })
      if (!res.ok) throw new Error(res.error.message)
      onCreated()
      toast({ title: "Customer mapping saved", description: `Future actualization runs can include this ${provider} customer.` })
    } catch (e: any) {
      const message = e?.message ?? "Failed"
      setErr(message)
      toast({ title: "Could not save customer mapping", description: message, variant: "destructive" })
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr className="border-t border-gray-100">
      <td className="px-4 py-2">
        <div>{customer.example_customer || customer.normalized_customer}</div>
        {customer.reason ? <p className="mt-0.5 text-xs text-gray-500">{customer.reason}</p> : null}
      </td>
      <td className="px-4 py-2 text-right">{customer.line_count}</td>
      <td className="px-4 py-2">
        <div className="flex flex-col gap-1">
          {top ? (
            <p className="text-xs text-gray-600">
              Top match:{" "}
              <span className="font-medium text-gray-800">
                {customerNodes.find((n) => n.stable_key === top.customer_stable_key)?.name ?? "Customer"}
              </span>{" "}
              ({Math.round(top.score * 100)}% — {top.match_summary})
            </p>
          ) : (
            <p className="text-xs text-gray-500">No strong hierarchy match — search to map manually.</p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <ForecastCustomerMapSelect
              nodes={customerNodes}
              suggestions={customer.suggested_customers}
              value={stableKey}
              onValueChange={setStableKey}
              className="flex-1 min-w-[260px]"
            />
            <Button size="sm" onClick={save} disabled={!stableKey || busy}>
              {busy ? "Saving…" : "Map"}
            </Button>
            {err && <span className="text-xs text-red-600">{err}</span>}
          </div>
        </div>
      </td>
    </tr>
  )
}

function ExistingCustomerAliasesCard({
  aliases,
  customerNodes,
  provider,
  onChanged,
}: {
  aliases: MappingCustomerAlias[]
  customerNodes: MappingCustomerNode[]
  provider: string
  onChanged: () => void
}) {
  const nameByKey = new Map(customerNodes.map((n) => [n.stable_key, n.name]))
  return (
    <CollapsibleSection
      title="Existing customer aliases"
      count={aliases.length}
      description={`${provider} customers already mapped to a forecast hierarchy node.`}
    >
      {aliases.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">No customer aliases yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 uppercase sticky top-0 z-10 bg-white shadow-[0_1px_0_0_theme(colors.gray.200)]">
            <tr>
              <th className="px-4 py-2 text-left">Alias</th>
              <th className="px-4 py-2 text-left">Forecast customer</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {aliases.map((a) => (
              <tr key={a.id} className="border-t border-gray-100">
                <td className="px-4 py-2">{a.alias}</td>
                <td className="px-4 py-2">{nameByKey.get(a.customer_stable_key) ?? a.customer_stable_key}</td>
                <td className="px-4 py-2 text-right">
                  <DeleteAliasButton url={`/forecast-mappings/customer-aliases/${a.id}`} onDeleted={onChanged} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CollapsibleSection>
  )
}

function DeleteAliasButton({ url, onDeleted }: { url: string; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false)
  async function go() {
    setBusy(true)
    try {
      const res = await api_fetch(url, { method: "DELETE" })
      if (!res.ok) throw new Error(res.error.message)
      onDeleted()
      toast({ title: "Mapping removed" })
    } catch (e: any) {
      toast({
        title: "Could not remove mapping",
        description: e?.message ?? "The mapping was not removed.",
        variant: "destructive",
      })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Button size="sm" variant="outline" onClick={go} disabled={busy}>
      {busy ? "…" : "Remove"}
    </Button>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function integration(name: string): IntegrationWithJobInfo {
    return { id: name, name } as unknown as IntegrationWithJobInfo
  }

  describe("mappings_provider_label", () => {
    it("names NetSuite when that is the connection and QuickBooks otherwise", () => {
      expect(mappings_provider_label([integration("gmail"), integration("netsuite")])).toBe("NetSuite")
      expect(mappings_provider_label([integration("quickbooks")])).toBe("QuickBooks")
      expect(mappings_provider_label([integration("gmail")])).toBe("QuickBooks")
      expect(mappings_provider_label(undefined)).toBe("QuickBooks")
    })
  })

  describe("mappings_apply_to_org", () => {
    it("applies on QuickBooks and NetSuite and names the other system otherwise", () => {
      expect(mappings_apply_to_org([integration("gmail"), integration("quickbooks")])).toEqual({ applies: true, accounting_system: "QuickBooks" })
      expect(mappings_apply_to_org([integration("netsuite")])).toEqual({ applies: true, accounting_system: "NetSuite" })
      expect(mappings_apply_to_org([integration("xero")])).toEqual({ applies: false, accounting_system: "Xero" })
      expect(mappings_apply_to_org([integration("gmail")])).toEqual({ applies: false, accounting_system: null })
    })

    it("assumes it applies while the integrations are still loading", () => {
      expect(mappings_apply_to_org(undefined).applies).toBe(true)
    })
  })
}
